import type { TicketMessageDto, TicketStatus } from "./contracts";

/** Deliberately excludes private notes, contact fields, CC, metadata and files. */
export interface TicketConversationExportMessageDto {
  id: string;
  authorType: TicketMessageDto["authorType"];
  authorName: string;
  createdAt: string;
  text: string;
  html: string | null;
  deliveryStatus?: TicketMessageDto["deliveryStatus"];
}

export interface TicketConversationExportDto {
  number: string;
  subject: string;
  status: TicketStatus;
  createdAt: string;
  exportedAt: string;
  messages: TicketConversationExportMessageDto[];
}

// Mask display addresses, not just the subset accepted by the account form:
// imported email bodies may contain quoted local parts or address literals.
// Do not retry from every character of a long invalid local part. Message
// bodies can be large, so a naive unanchored `local+@domain` can be quadratic.
// Consumed boundary captures work in older Safari without regex lookbehind.
const exportEmailPattern = /(?:(^|[^\\])("(?:[^"\\\r\n]|\\[^\r\n])+")|(^|[^\p{L}\p{N}.!#$%&'*+/=?^_`{|}~-])([\p{L}\p{N}.!#$%&'*+/=?^_`{|}~-]+))((?:@|%40)(?:[\p{L}\p{N}-]+(?:(?:\.|%2e)[\p{L}\p{N}-]+)+|\[(?:IPv6:)?[0-9a-f:.]+\]))/giu;

/** Shared positions let the print renderer redact across multiple inline nodes. */
export function exportEmailMatches(value: string): RegExpMatchArray[] {
  if (!value.includes("@") && !/%40/i.test(value)) return [];
  const pattern = new RegExp(exportEmailPattern.source, exportEmailPattern.flags);
  const matches: RegExpMatchArray[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(value))) {
    const prefix = match[1] ?? match[3] ?? "";
    const address = (match[2] ?? match[4]) + match[5];
    const start = match.index + prefix.length;
    matches.push(Object.assign([address] as [string], { index: start, input: value }));
    // Re-use the final character as a possible boundary for an immediately
    // adjacent quoted address. Keep indices in original UTF-16 coordinates.
    const end = start + address.length;
    const trailing = value.charCodeAt(end - 1);
    pattern.lastIndex = end - (trailing >= 0xdc00 && trailing <= 0xdfff ? 2 : 1);
  }
  return matches;
}

/** The print renderer also masks decoded DOM text, including split inline nodes. */
export function maskExportEmails(value: string): string {
  const chunks: string[] = [];
  let previousEnd = 0;
  for (const match of exportEmailMatches(value)) {
    chunks.push(value.slice(previousEnd, match.index!), "[email hidden]");
    previousEnd = match.index! + match[0].length;
  }
  return chunks.join("") + value.slice(previousEnd);
}
